import { useRef, useState } from "react";

export default function ExtendLoanModal({ isOpen, dueDate = "2026-03-24", onCancel, onConfirm, newExtendDate, setNewExtendDate }) {
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const inputRef = useRef(null);

  if (!isOpen) return null;

  const due = new Date(dueDate);
  const picked = newExtendDate ? new Date(newExtendDate) : null;
  const isOver = picked && picked <= due;
  const isValid = picked && picked > due;

  const days = isValid ? Math.round((picked - due) / (1000 * 60 * 60 * 24)) : null;

  const hint = isOver ? `New date must be after ${dueDate}` : isValid ? `Extends loan by ${days} day${days !== 1 ? "s" : ""}` : "";

  const handleConfirm = async () => {
    if (!isValid) return;
    setLoading(true);
    await onConfirm?.({ newDueDate: newExtendDate });
    setLoading(false);
    setDone(true);
  };

  const openPicker = () => {
    try {
      inputRef.current?.showPicker();
    } catch (_) {
      inputRef.current?.click();
    }
  };

  return (
    <div onClick={onCancel} style={s.overlay}>
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
        style={s.modal}
      >
        {!done ? (
          <>
            {/* Header */}
            <div style={s.header}>
              <div style={s.iconWrap}>
                <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='#4A90E2' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                  <rect x='3' y='4' width='18' height='18' rx='2' />
                  <line x1='16' y1='2' x2='16' y2='6' />
                  <line x1='8' y1='2' x2='8' y2='6' />
                  <line x1='3' y1='10' x2='21' y2='10' />
                </svg>
              </div>
              <p style={s.title}>Extend loan due date</p>
              <p style={s.due}>
                Loan is due on <span style={s.dueVal}>{dueDate}</span>
              </p>
            </div>

            {/* Body */}
            <div style={s.body}>
              <label style={s.label}>New due date</label>
              <div style={s.inputWrap} onClick={openPicker}>
                <input
                  ref={inputRef}
                  type='date'
                  value={newExtendDate}
                  onChange={(e) => setNewExtendDate(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    ...s.input,
                    borderColor: isOver ? "#fca5a5" : newExtendDate && isValid ? "#bfdbfe" : "#e5e7eb",
                    background: isOver ? "#fff5f5" : "#f9fafb",
                  }}
                  onFocus={(e) => {
                    if (!isOver) e.target.style.borderColor = "#bfdbfe";
                    e.target.style.background = "#ffffff";
                  }}
                  onBlur={(e) => {
                    if (!isOver) e.target.style.borderColor = newExtendDate && isValid ? "#bfdbfe" : "#e5e7eb";
                    if (!isOver) e.target.style.background = isOver ? "#fff5f5" : "#f9fafb";
                  }}
                />
                <span style={s.calIcon}>
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#9ca3af' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                    <rect x='3' y='4' width='18' height='18' rx='2' />
                    <line x1='16' y1='2' x2='16' y2='6' />
                    <line x1='8' y1='2' x2='8' y2='6' />
                    <line x1='3' y1='10' x2='21' y2='10' />
                  </svg>
                </span>
              </div>
              <p style={{ ...s.hint, color: isOver ? "#ef4444" : isValid ? "#15803d" : "#6b7280" }}>{hint}</p>
            </div>

            {/* Footer */}
            <div style={s.footer}>
              <button
                onClick={onCancel}
                disabled={loading}
                style={s.cancelBtn}
                onMouseOver={(e) => (e.currentTarget.style.background = "#f9fafb")}
                onMouseOut={(e) => (e.currentTarget.style.background = "#ffffff")}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!isValid || loading}
                style={{
                  ...s.confirmBtn,
                  background: !isValid || loading ? "#93c5fd" : "#4A90E2",
                  cursor: !isValid || loading ? "not-allowed" : "pointer",
                }}
                onMouseOver={(e) => {
                  if (isValid && !loading) e.currentTarget.style.background = "#3a7bc8";
                }}
                onMouseOut={(e) => {
                  if (isValid && !loading) e.currentTarget.style.background = "#4A90E2";
                }}
              >
                {loading ? (
                  <span style={s.spinnerWrap}>
                    <span style={s.spinner} />
                    Extending…
                  </span>
                ) : (
                  "Extend loan"
                )}
              </button>
            </div>
          </>
        ) : (
          <div style={s.success}>
            <div style={s.successIcon}>
              <svg width='28' height='28' viewBox='0 0 24 24' fill='none' stroke='#15803d' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                <polyline points='20 6 9 17 4 12' />
              </svg>
            </div>
            <p style={s.successTitle}>Due date extended</p>
            <p style={s.successSub}>New due date set to {newExtendDate}</p>
          </div>
        )}
      </div>
      <style>{`
        @keyframes elm-spin { to { transform: rotate(360deg); } }
        input[type="date"]::-webkit-calendar-picker-indicator {
          position: absolute; top: 0; left: 0;
          width: 100%; height: 100%;
          opacity: 0; cursor: pointer; z-index: 2;
        }
        input[type="date"] { color-scheme: light; }
      `}</style>
    </div>
  );
}

const s = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "16px",
  },
  modal: {
    background: "#ffffff",
    borderRadius: "16px",
    width: "100%",
    maxWidth: "400px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
    overflow: "hidden",
  },
  header: {
    padding: "28px 28px 0",
    background: "#ffffff",
    textAlign: "center",
  },
  iconWrap: {
    width: "44px",
    height: "44px",
    borderRadius: "50%",
    background: "#eff6ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 14px",
  },
  title: {
    fontSize: "17px",
    fontWeight: 600,
    color: "#111827",
    margin: "0 0 4px",
  },
  due: {
    fontSize: "13px",
    color: "#6b7280",
    margin: 0,
  },
  dueVal: {
    fontWeight: 600,
    color: "#4A90E2",
  },
  body: {
    padding: "20px 28px",
    background: "#ffffff",
  },
  label: {
    fontSize: "13px",
    color: "#6b7280",
    fontWeight: 500,
    marginBottom: "6px",
    display: "block",
  },
  inputWrap: {
    position: "relative",
    cursor: "pointer",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    fontSize: "14px",
    color: "#111827",
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: "10px",
    outline: "none",
    boxShadow: "none",
    appearance: "none",
    WebkitAppearance: "none",
    cursor: "pointer",
    transition: "border-color 0.15s, background 0.15s",
    position: "relative",
    zIndex: 1,
  },
  calIcon: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none",
    zIndex: 0,
    display: "flex",
  },
  hint: {
    fontSize: "12px",
    minHeight: "18px",
    marginTop: "6px",
  },
  footer: {
    borderTop: "1px solid #f3f4f6",
    padding: "16px 28px",
    display: "flex",
    gap: "10px",
    background: "#ffffff",
  },
  cancelBtn: {
    flex: 1,
    padding: "10px",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    borderRadius: "8px",
    background: "#ffffff",
    border: "1px solid #d1d5db",
    color: "#374151",
    transition: "background 0.15s",
  },
  confirmBtn: {
    flex: 1,
    padding: "10px",
    fontSize: "14px",
    fontWeight: 600,
    borderRadius: "8px",
    border: "none",
    color: "#ffffff",
    transition: "background 0.15s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  spinnerWrap: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  spinner: {
    width: "14px",
    height: "14px",
    border: "2px solid rgba(255,255,255,0.35)",
    borderTopColor: "#ffffff",
    borderRadius: "50%",
    animation: "elm-spin 0.6s linear infinite",
    display: "inline-block",
    flexShrink: 0,
  },
  success: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 28px",
    textAlign: "center",
    background: "#ffffff",
  },
  successIcon: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    background: "#f0fdf4",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "16px",
  },
  successTitle: {
    fontSize: "17px",
    fontWeight: 600,
    color: "#111827",
    marginBottom: "6px",
  },
  successSub: {
    fontSize: "14px",
    color: "#6b7280",
    margin: 0,
  },
};
